import { useCallback, useEffect } from 'react';

import { useFeatureFlagEnabled } from 'posthog-js/react';
import { useNavigate, useParams } from 'react-router';

import { PERSONALIZATION_FLAG } from '@/constants/featureFlags';
import { DASHBOARD_PREFIX } from '@/features/Dashboard/constants/const';
import { useAppSelector } from '@/hooks/redux-hooks';

import { useMarketContext } from '../context/MarketContext';

export const useMarketNavigation = () => {
  const params = useParams();
  const navigate = useNavigate();

  const market = useAppSelector(state => state.markets.markets);
  const { setActiveTab } = useMarketContext();

  const { marketsList, status: marketListStatus } = useAppSelector(state => state.marketsList);
  const { userConfig, status: userConfigStatus } = useAppSelector(state => state.userConfig);

  const defaultMarketId = userConfig?.defaultMarket?.value;
  const homepage = userConfig?.homepage?.value;

  const isMarketValid = useCallback(
    (marketId: string) => marketsList.some(market => market.marketId === marketId),
    [marketsList]
  );

  const isPersonalizationEnabled = useFeatureFlagEnabled(PERSONALIZATION_FLAG);

  useEffect(() => {
    if (
      marketListStatus !== 'fulfilled' ||
      userConfigStatus === 'idle' ||
      userConfigStatus === 'loading' ||
      !marketsList.length ||
      isPersonalizationEnabled === undefined
    ) {
      return;
    }

    if (!params.marketId) {
      if (isPersonalizationEnabled && homepage) {
        // Homepage set to the dashboard root
        if (homepage === 'my-dashboard') {
          navigate(`/dashboard`);
          return;
        }

        if (homepage.startsWith(DASHBOARD_PREFIX)) {
          const dashboardId = homepage.replace(DASHBOARD_PREFIX, '');
          navigate(`/dashboard/${encodeURIComponent(dashboardId)}`);
          return;
        }

        if (isMarketValid(homepage)) {
          navigate(`/markets/${homepage}`, { replace: true });
          return;
        }
      }

      const targetMarketId = isMarketValid(defaultMarketId)
        ? defaultMarketId
        : marketsList[0].marketId;
      navigate(`/markets/${targetMarketId}`, { replace: true });

      return;
    }

    if (!isMarketValid(params.marketId)) {
      navigate('/notfound');
    }
  }, [
    marketListStatus,
    userConfigStatus,
    marketsList,
    defaultMarketId,
    params.marketId,
    navigate,
    homepage,
    isPersonalizationEnabled,
    isMarketValid
  ]);

  const handleTabNavigation = useCallback(() => {
    if (!market || !params.marketId) return;

    const normalizedMarketId = market.id.toLowerCase();
    const currentMarketId = params.marketId.toLowerCase();

    if (normalizedMarketId !== currentMarketId) return;

    const hasPages = market.pages && market.pages.length > 0;

    if (!hasPages) return; // Explicitly return if no pages

    const correctMarketIdInUrl = params.marketId !== market.id;
    const tabId = params.tabId ?? market.pages[0].id;

    if (correctMarketIdInUrl || !params.tabId) {
      navigate(`/markets/${market.id}/${tabId}`, { replace: true });
      return;
    }

    const activeTabIndex = market.pages.findIndex(page => page.id === params.tabId);
    if (activeTabIndex !== undefined && activeTabIndex >= 0) {
      setActiveTab(activeTabIndex);
    }
  }, [market, params, navigate, setActiveTab]);

  useEffect(() => {
    handleTabNavigation();
  }, [handleTabNavigation]);
};
